/**
 * Task Hooks
 *
 * Provides hooks for task operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { TaskAudience } from '@/features/tasks/taskAudience';

export type { TaskAudience };

// Types
type TaskStatus = 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
type TaskType = 'general' | 'waiting_parts';

export interface TaskListItem {
  id: string;
  title: string;
  description: string | null;
  note: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  audience?: TaskAudience;
  assignedTo: { id: string; firstName: string; lastName: string } | null;
  createdBy: { id: string; firstName: string; lastName: string };
  booking: { id: string; vehicleRegNumber: string; serviceType: string; status: string | null } | null;
  scheduledAt: string | null;
  estimatedDurationHours: number | null;
  deadline: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  type?: TaskType;
  assignedToId?: string;
  unassigned?: boolean;
  openOnly?: boolean;
  audience?: TaskAudience;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'scheduledAt' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

interface CreateTaskData {
  title: string;
  description?: string;
  note?: string | null;
  priority?: TaskPriority;
  audience?: TaskAudience;
  assignedToId?: string | null;
  bookingId?: string | null;
  scheduledAt?: string | null;
  estimatedDurationHours?: number | null;
  deadline?: string | null;
}

type UpdateTaskData = Partial<CreateTaskData>;

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

/**
 * Hook to fetch paginated list of tasks with infinite scroll
 */
export function useTasksList(
  filters: TaskFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.tasks.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<TaskListItem>>('/tasks', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 15 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a single task by ID
 */
export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.detail(id ?? ''),
    queryFn: () => api.get<TaskListItem>(`/tasks/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to create a task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskData) =>
      api.post<TaskListItem>('/tasks', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to update a task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskData }) =>
      api.patch<TaskListItem>(`/tasks/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to start a task
 */
export function useStartTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/start`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to pause a task
 */
export function usePauseTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/pause`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to resume a task
 */
export function useResumeTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/resume`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to complete a task
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/complete`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}

/**
 * Hook to claim an unassigned task
 */
export function useClaimTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/tasks/${id}/claim`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}
